import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef } from "react";
import { aggridHeaderClass, titleMRClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, createCellCssClassRules, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, GridCellConfig, postSortHandler, RemoveRowHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell } from "src/utils/grid/gridStyleUtils";
import { useWMS1020_R01Store } from "./Store_WMS1020_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { WMS1020_R01LotDto, WMS1020_R01DetailDto } from "./Dto_WMS1020_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";
import { getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";

// 범용 훅 임포트
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";

const COLUMN_STATE_KEY = 'WMS1020_R01-lot-grid-column-state';

interface Props {
	lotGridRef: React.RefObject<AgGridReact<WMS1020_R01LotDto> | null>;
	defaultWMS1020_R01LotRow: WMS1020_R01LotDto;
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	handleInsertClick: (e: React.MouseEvent, rowIndex?: number) => void;
}

export const LotTable_WMS1020_R01 = (props: Props) => {

	const { lotGridRef, defaultWMS1020_R01LotRow, clickable, setClickable, handleInsertClick } = props;

	const {
		WMS1020_R01LotMap,
		setWMS1020_R01LotList,
		updateWMS1020_R01LotList,
		removeWMS1020_R01LotList,
		selectedWMS1020_R01LotList, addSelectedWMS1020_R01LotList, removeSelectedWMS1020_R01LotList,
		initSelectedWMS1020_R01LotList,
		deletedWMS1020_R01LotList, addDeletedWMS1020_R01LotList,
		originalWMS1020_R01LotList,
		selectedItemCdForLot,
		lotColumnState, setLotColumnState,
		lotScrollTop, setLotScrollTop,
		editWMS1020_R01,
	} = useWMS1020_R01Store();
	const { itemStatusList } = useCommonCodeStore();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const gridData = useMemo(
		() => WMS1020_R01LotMap.get(selectedItemCdForLot || '') || [],
		[WMS1020_R01LotMap, selectedItemCdForLot],
	);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef: lotGridRef,
		key: 'valueIndex',
		gridData,
		defaultGridData: defaultWMS1020_R01LotRow,
		selectedRow: selectedWMS1020_R01LotList,
		originalGridData: originalWMS1020_R01LotList,
		updateRow: (index: number, data: any) => updateWMS1020_R01LotList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		purchaseLotNo: 'Lot No',
		lotQty: '수량',
		lotInputOption: 'LOT입력옵션',
	};

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['purchaseLotNo'],
		insertedDisabledCells: [''],
		commonDisabledCells: [''],
		
		pointerCells: ['lotQty', 'lotInputOption'],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		
		alignLeftCells: ['']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<WMS1020_R01LotDto>({
            idKey: 'valueIndex',
            selectedList: selectedWMS1020_R01LotList,
            deletedList: deletedWMS1020_R01LotList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }};
    }, [
		
        selectedWMS1020_R01LotList, deletedWMS1020_R01LotList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns, createCellClassRules,
	]);

	const commonInputColumns = ['lotQty', 'lotInputOption'];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['lotQty', 'lotInputOption'];

	const baseColumnDefs: ColDef<WMS1020_R01LotDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: WMS1020_R01DetailDto) => renderIndexCell(
				params, 
				(innerParams: WMS1020_R01LotDto) => getRowStyleFlags(
					innerParams,
					{
						deletedList: deletedWMS1020_R01LotList,
						selectedList: selectedWMS1020_R01LotList,
						dataList: WMS1020_R01LotMap.get(selectedItemCdForLot || '') || []
					}),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { handleInsertClick(e, rowIndex); }
					} else { 
						handleInsertClick(e, rowIndex);
					}
				},
				(rowData) => RemoveRowHandler( 
					lotGridRef,
					gridData, 
					rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
					setClickable, 
					removeWMS1020_R01LotList, 
					(valueIndex) => addDeletedWMS1020_R01LotList([valueIndex.toString()])
				),
			)
		),
		{
			headerName: 'Lot No', colId: 'purchaseLotNo', field: 'purchaseLotNo', width: 130,
			editable: false,
		},
		{
			headerName: '입고량', colId: 'lotQty', field: 'lotQty', width: 65,
			editable: (params: any) => params.data.inserted === true,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,

		},
		// {
		// 	headerName: '직접입력', colId: 'lotInputOption', field: 'lotInputOption', width: 75,
		// 	...getCommonCodeColumnOptions(yesNoList, (params: any) => params.data.inserted === true, 'string'),
		// },
		// {
		// 	headerName: '출력 여부', colId: 'printed', field: 'printed', width: 50,
		// 	editable: false,
		// 	cellRenderer: (params: any) => params.value ? 'V' : '',
		// 	cellStyle: (params: any) => ({
		// 		textAlign: 'center',
		// 		fontWeight: params.value ? 'bold' : 'normal',
		// 		color: params.value ? '#10b981' : '#6b7280'
		// 	}),
		// },
		// ...IndexParameterCell(),
	], [
		deletedWMS1020_R01LotList, gridData, selectedItemCdForLot, selectedWMS1020_R01LotList, handleInsertClick, createCellClassRules,
	]);

	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

const columnDefs: ColDef<WMS1020_R01LotDto>[] = useMemo(() => {
		if (lotColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<WMS1020_R01LotDto>({
			baseColumnDefs,
			columnState: lotColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, lotColumnState, headerNames]);

	
	const handleCellChange = useCellChangeHandler({
		dataList: gridData,
		originalDataList: originalWMS1020_R01LotList,
		updateRow: updateWMS1020_R01LotList,
		customBeforeUpdate: (updatedData, field, value) => {
			return updatedData;
		},
		customAfterUpdate: (updatedData, field, value) => {
			return updatedData;
		},
	});
	

	const handleRowClick = useRowSelectionHandler({
		dataList: gridData,
		idKey: 'valueIndex',
		selectedList: selectedWMS1020_R01LotList,
		deletedList: deletedWMS1020_R01LotList,
		addSelected: (ids) => {
			// console.log(' LOT 선택 추가:', ids);
			addSelectedWMS1020_R01LotList(ids);
		},
		removeSelected: (ids) => {
			// console.log(' LOT 선택 해제:', ids);
			removeSelectedWMS1020_R01LotList(ids);
		},
		clickable, setClickable
	});


	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: gridData as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});
	
	const cellClickActions: CustomCellClickAction<WMS1020_R01LotDto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				return commonInputColumns.includes(field);
			},
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(gridData, 'valueIndex', cellClickActions);

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<WMS1020_R01LotDto>[] = useMemo(() => [
	], [editWMS1020_R01?.warehouseCd]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(gridData, 'valueIndex', cellDoubleClickActions);


	const handlePostSort = useCallback((params: PostSortRowsParams<WMS1020_R01LotDto>) => {
		postSortHandler(params, gridData, originalWMS1020_R01LotList, setWMS1020_R01LotList);
	}, [gridData, originalWMS1020_R01LotList, setWMS1020_R01LotList]);

	const pinnedBottomRowData = useMemo(() => {
		return [{
			lotQty: gridData.reduce((sum, item) => sum + (deletedWMS1020_R01LotList.includes(item.valueIndex.toString()) ? 0 : Number(item.lotQty) || 0), 0),
		}];	  
	}, [gridData, deletedWMS1020_R01LotList,]);

	// 선택된 LOT 행 스타일 (출력 대기 표시)
	const getRowStyle = useCallback((params: any) => {
		if (!params.data || params.node.rowPinned) return undefined;
		
		const valueIndex = String(params.data.valueIndex);
		const isSelectedForPrint = selectedWMS1020_R01LotList.includes(valueIndex);
		const isPrinted = params.data.printed === true;
		
		// console.log(' getRowStyle 실행:', { valueIndex, isSelectedForPrint, isPrinted });
		
		// if (isSelectedForPrint && !isPrinted) {
		// 	// 출력 대기 상태 (선택됨 + 미출력)
		// 	return { 
		// 		backgroundColor: '#e3f2fd !important',  // 연한 파란색 (우선순위 높임)
		// 		borderLeft: '3px solid #2196f3'
		// 	};
		// }
		
		return undefined;
	}, [selectedWMS1020_R01LotList]);

	return (
		<>
			<GridContextWrapper<WMS1020_R01LotDto>
				enableCellCalculation
				gridWrapperRef={gridWrapperRef}
				gridRef={lotGridRef}
				paddedGridData={paddedGridData}
				setPaddedGridData={setWMS1020_R01LotList}
				initSelected={initSelectedWMS1020_R01LotList}
				defaultRowData={defaultWMS1020_R01LotRow}
				baseColumnDefs={lotColumnState ? columnDefs : baseColumnDefs}
				defaultColumnState={defaultColumnState}	
				setColumnState={setLotColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Excel<WMS1020_R01LotDto>
					ref={lotGridRef}
					rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
					columnDefs={columnDefs}		
					
					defaultColDef={cellCssDefaultColDef}
					rowSelection="multiple"

					rowClassRules={createRowClassRules<WMS1020_R01LotDto>({
						idKey: 'valueIndex',
						selectedList: selectedWMS1020_R01LotList,
						deletedList: deletedWMS1020_R01LotList
					})}
					getRowStyle={getRowStyle}

					onRowClicked={handleRowClick}
					onCellClicked={handleCellClick}
					onCellDoubleClicked={handleCellDoubleClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellValueChanged={handleCellChange}
					pinnedBottomRowData={pinnedBottomRowData}
					

					onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
					// Grid_Select에 공통 로직 처리를 위한 props 전달
					columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
					setColumnState={setLotColumnState}		//컬럼 상태 저장
					getDefaultColumnState={() => defaultColumnState}
					scrollTop={lotScrollTop}
					setScrollTop={setLotScrollTop}
				/>
			</GridContextWrapper>
		</>
	)
}	
